import { lazy } from "react";
import { Router, Switch, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { RecoilRoot } from "recoil";
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  ApolloProvider,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

import { Main } from "./main";
import history from "./history";
import theme from "./theme/themeThree";
import { UserStatePreFill } from "./MVVM/Hooks/userStatePreFill";
import { GlobalStyles } from "./theme/globalStyles";
import { AdsProvider, RampProvider } from "./MVVM/Providers";
import { GamePreferenceInitializer } from "MVVM/Providers/GamePreferenceInitializer";
import { PageViewTracker } from "MVVM/Analytics";

const { REACT_APP_GQL_ENDPOINT } = process.env;

const LazyEmbeddablePlayer = lazy(() =>
  import("./MVVM/Views/EmbeddablePlayer"),
);
const LazyEmbeddablePlaylist = lazy(() =>
  import("./MVVM/Views/EmbeddablePlaylist"),
);

const RAMP_PARTNERS = ["UDOTGG", "U.GG"];

const IframeComponent = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const playlist = searchParams.get("playlist");
  const platform = searchParams.get("platform") || "";
  const testRamp = searchParams.get("testRamp") === "true";
  const isRampPartner = testRamp && RAMP_PARTNERS.includes(platform);

  console.log(
    "[Ramp:App] platform:",
    platform,
    "testRamp:",
    testRamp,
    "isRampPartner:",
    isRampPartner,
  );

  const content = playlist ? (
    <LazyEmbeddablePlaylist playlist={playlist} />
  ) : (
    <LazyEmbeddablePlayer />
  );

  if (isRampPartner) {
    return <RampProvider>{content}</RampProvider>;
  }

  return content;
};

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      /*
      TODO:
      Add logzio logging
      */
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem("token");
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      authorization: token ? `Bearer ${token}` : "",
      ...headers,
    },
  };
});

const httpLink = createHttpLink({
  uri: REACT_APP_GQL_ENDPOINT,
});

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Promotions: {
        keyFields: ["name"],
      },
    },
  }),
  link: authLink.concat(errorLink).concat(httpLink),
});

export default function () {
  return (
    <ApolloProvider client={apolloClient}>
      <RecoilRoot id="root">
        <AdsProvider>
          <ThemeProvider theme={theme}>
            <GlobalStyles />
            <Router history={history}>
              <PageViewTracker />
              <GamePreferenceInitializer />
              <UserStatePreFill force={false} />
              <Switch>
                <Route exact path="/iframe" component={IframeComponent} />
                <Main />
              </Switch>
            </Router>
          </ThemeProvider>
        </AdsProvider>
      </RecoilRoot>
    </ApolloProvider>
  );
}
